import {
  Button,
  Heading,
  HStack,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { LuChevronDown, LuChevronRight, LuChevronUp } from 'react-icons/lu';
import { getCustomerOrders } from 'services/order';
import { R$ } from 'shared/helpers/currency';
import { Order, orderStatus } from 'shared/models/order';
import { useFinancialContext } from '../../FinancialContext';
import ContractDataSection from './ContractDataSection';

const VISIBLE_PER_CONTRACT = 2;
const FETCH_LIMIT = 100;
const DAYS_AHEAD = 10;

interface OpenOrderGroup {
  contractId?: number;
  orders: Order[];
}

const CustomerOpenOrdersCard = () => {
  const { isActive, openOrderModal } = useFinancialContext();
  const { customer } = useCustomerContext();
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [totalOpen, setTotalOpen] = useState(0);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const loadedCustomerId = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive || !customer?.id) return;
    if (loadedCustomerId.current === customer.id) return;

    loadedCustomerId.current = customer.id;
    setLoading(true);
    setFailed(false);
    setExpanded([]);

    getCustomerOrders(customer.id, {
      statusId: orderStatus.EM_ABERTO,
      orderBy: 'asc',
      limit: FETCH_LIMIT,
      offset: 0,
      endDate: moment().add(DAYS_AHEAD, 'days').format('YYYY-MM-DD'),
    })
      .then((response) => {
        setOrders(response.data.results.map((order) => new Order(order)));
        setTotal(response.data.total);
        setTotalOpen(response.data.summary.totalOpenAmount);
      })
      .catch((e: AxiosError) => {
        console.error(
          'Erro ao carregar as cobranças em aberto do cliente',
          e?.response?.data
            ? JSON.stringify(e?.response?.data, null, 2)
            : JSON.stringify(e?.response, null, 2),
        );
        setOrders([]);
        setTotal(0);
        setTotalOpen(0);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, [isActive, customer?.id]);

  const groups = useMemo<OpenOrderGroup[]>(() => {
    const byContract = new Map<number, OpenOrderGroup>();

    orders.forEach((order) => {
      const key = order.contractId ?? 0;
      const group = byContract.get(key);

      if (group) group.orders.push(order);
      else
        byContract.set(key, { contractId: order.contractId, orders: [order] });
    });

    return Array.from(byContract.values());
  }, [orders]);

  const toggleContract = (contractId: number) =>
    setExpanded((current) =>
      current.includes(contractId)
        ? current.filter((id) => id !== contractId)
        : [...current, contractId],
    );

  const isOverdue = (order: Order) =>
    !!order.due_date && order.due_date.isBefore(moment(), 'day');

  return (
    <ContractDataSection
      title={
        <HStack w="100%" justify="space-between" align="flex-start">
          <Stack spacing="0">
            <Heading variant="small">Cobranças em aberto</Heading>
            <Text variant="regular" color="yGray.500">
              Vencidas e a vencer em até {DAYS_AHEAD} dias
            </Text>
          </Stack>
          {!failed && !loading && (
            <Text variant="regular" color="yGray.500">
              Total: {R$(totalOpen)}
            </Text>
          )}
        </HStack>
      }
    >
      <Skeleton isLoaded={!loading}>
        {failed && (
          <Text variant="regular" color="yRed.600">
            Não foi possível carregar as cobranças em aberto
          </Text>
        )}

        {!failed && !groups.length && (
          <Text variant="regular" color="yGray.500">
            Nenhuma cobrança vencida ou a vencer em até {DAYS_AHEAD} dias
          </Text>
        )}

        {!failed && !!groups.length && (
          <>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th w="25%" textAlign="left">
                    Contrato
                  </Th>
                  <Th w="25%" textAlign="left">
                    Vencimento
                  </Th>
                  <Th w="25%" textAlign="left">
                    Valor
                  </Th>
                  <Th w="25%" textAlign="left">
                    Forma de pagamento
                  </Th>
                  <Th textAlign="left"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {groups.map((group) => {
                  const contractId = group.contractId ?? 0;
                  const isExpanded = expanded.includes(contractId);
                  const visibleOrders = isExpanded
                    ? group.orders
                    : group.orders.slice(0, VISIBLE_PER_CONTRACT);
                  const hiddenCount =
                    group.orders.length - visibleOrders.length;

                  return (
                    <Fragment key={contractId}>
                      {visibleOrders.map((order, index) => (
                        <Tr key={order.id}>
                          <Td w="25%" textAlign="left">
                            {index === 0 && (
                              <Text>{group.contractId ?? '-'}</Text>
                            )}
                          </Td>
                          <Td w="25%" textAlign="left">
                            <Text color={isOverdue(order) ? 'yRed.600' : ''}>
                              {order.due_date?.format('DD/MM/YYYY')}
                            </Text>
                            {isOverdue(order) && (
                              <Text variant="regular" color="yRed.600">
                                Atrasado
                              </Text>
                            )}
                          </Td>
                          <Td w="25%" textAlign="left">
                            {order.priceString}
                          </Td>
                          <Td w="25%" textAlign="left">
                            {order.bankId === 1
                              ? 'Cartão de crédito'
                              : 'Boleto Bancário'}
                          </Td>
                          <Td textAlign="left">
                            <Button
                              variant="plainIcon"
                              onClick={() => openOrderModal(order.token)}
                            >
                              <LuChevronRight />
                            </Button>
                          </Td>
                        </Tr>
                      ))}

                      {(hiddenCount > 0 || isExpanded) && (
                        <Tr>
                          <Td colSpan={5} textAlign="left">
                            <Button
                              variant="plainIcon"
                              onClick={() => toggleContract(contractId)}
                            >
                              {isExpanded ? <LuChevronUp /> : <LuChevronDown />}
                              <Text
                                variant="regular"
                                color="yPrimary.600"
                                fontWeight={500}
                                ml="1"
                              >
                                {isExpanded
                                  ? 'ver menos'
                                  : `mais ${hiddenCount} em aberto`}
                              </Text>
                            </Button>
                          </Td>
                        </Tr>
                      )}
                    </Fragment>
                  );
                })}
              </Tbody>
            </Table>

            {total > orders.length && (
              <Text variant="regular" color="yGray.500" mt="4">
                Mostrando as {orders.length} cobranças mais antigas de {total}.
              </Text>
            )}
          </>
        )}
      </Skeleton>
    </ContractDataSection>
  );
};

export default CustomerOpenOrdersCard;
